"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon,
  MapPinIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { INDIAN_STATES, getCitiesByStateName } from "@/lib/indian-locations";
import LegalDocumentModal from "@/components/legal/LegalDocumentModal";
import LoadingSpinner from "@/components/common/LoadingSpinner";

// Import types but use local definitions for UI

// Essential instructor types for quick selection - matching types/instructor.ts
const INSTRUCTOR_TYPES = [
  { value: "yogaInstructor", label: "Yoga Instructor" },
  { value: "meditationCoach", label: "Meditation Coach" },
  { value: "pranayamaCoach", label: "Pranayama Coach" },
  { value: "zumbaInstructor", label: "Zumba Instructor" },
  { value: "nutritionist", label: "Nutritionist" },
  { value: "ayurvedaPractitioner", label: "Ayurveda Practitioner" },
  { value: "womenWellnessCoach", label: "Women's Wellness Coach" },
  { value: "selfDefenseInstructor", label: "Self Defense Instructor" },
];

interface SimpleRegistrationForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  gender: string;
  dateOfBirth: string;
  city: string;
  state: string;
  instructorType: string[];
  yearsOfExperience: string;
  termsAccepted: boolean;
}

const InstructorRegisterPage = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDocType, setLegalDocType] = useState<"terms" | "privacy">(
    "terms"
  );
  const [isSuccess, setIsSuccess] = useState(false);

  // Custom dropdown states
  const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  // Add errors state to track validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Add server error state to show API errors
  const [serverError, setServerError] = useState<string>("");

  // Phone OTP verification state
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpValue, setPhoneOtpValue] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);
  const [phoneOtpError, setPhoneOtpError] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (phoneResendCooldown <= 0) return;
    const t = setTimeout(() => setPhoneResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneResendCooldown]);

  const [formData, setFormData] = useState<SimpleRegistrationForm>({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    gender: "",
    dateOfBirth: "",
    city: "",
    state: "",
    instructorType: [],
    yearsOfExperience: "",
    termsAccepted: false,
  });

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handleInputChange = (
    field: keyof SimpleRegistrationForm,
    value: string | boolean | string[]
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Calculate password strength
      if (field === "password") {
        setPasswordStrength(calculatePasswordStrength(value as string));
      }

      // Reset phone verification if phone number changes
      if (field === "phoneNumber") {
        setPhoneVerified(false);
        setPhoneOtpSent(false);
        setPhoneOtpValue("");
        setPhoneOtpError("");
      }

      // Handle state change to update available cities
      if (field === "state") {
        const cities = getCitiesByStateName(value as string);
        setAvailableCities(cities);
        // Reset city when state changes
        updated.city = "";
      }

      return updated;
    });

    // Clear error for this specific field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInstructorTypeToggle = (type: string) => {
    const newInstructorTypes = formData.instructorType.includes(type)
      ? formData.instructorType.filter((t) => t !== type)
      : [...formData.instructorType, type];

    setFormData((prev) => ({
      ...prev,
      instructorType: newInstructorTypes,
    }));

    // Clear error for instructor type when user makes a selection
    if (errors.instructorType) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.instructorType;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate full name
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please enter your full name";
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Please enter a password";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Validate phone number
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Please enter your phone number";
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ""))) {
      newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
    }

    // Validate gender
    if (!formData.gender) {
      newErrors.gender = "Please select your gender";
    }

    // Validate date of birth
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Please enter your date of birth";
    } else {
      // Validate age (must be at least 18)
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        age < 18 ||
        (age === 18 && monthDiff < 0) ||
        (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        newErrors.dateOfBirth =
          "You must be at least 18 years old to register as an instructor";
      }
    }

    // Validate city
    if (!formData.city.trim()) {
      newErrors.city = "Please enter your city";
    }

    // Validate state
    if (!formData.state) {
      newErrors.state = "Please select your state";
    }

    // Validate instructor type
    if (formData.instructorType.length === 0) {
      newErrors.instructorType = "Please select at least one instructor type";
    }

    // Validate years of experience
    if (!formData.yearsOfExperience) {
      newErrors.yearsOfExperience = "Please enter your years of experience";
    } else {
      const years = parseInt(formData.yearsOfExperience);
      if (isNaN(years) || years < 0) {
        newErrors.yearsOfExperience = "Please enter a valid number";
      } else if (years > 100) {
        newErrors.yearsOfExperience =
          "Please enter a realistic number of years";
      }
    }

    // Validate terms acceptance
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = "Please accept the Terms & Conditions";
    }

    // Set errors state
    console.log("Setting errors:", newErrors);
    setErrors(newErrors);

    // Return true if no errors
    const hasErrors = Object.keys(newErrors).length > 0;
    console.log(
      "Has errors:",
      hasErrors,
      "Error count:",
      Object.keys(newErrors).length
    );
    return !hasErrors;
  };

  const handleSendPhoneOtp = async () => {
    const digits = formData.phoneNumber.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setPhoneOtpError("Please enter a valid 10-digit Indian mobile number first");
      return;
    }
    setPhoneOtpError("");
    setPhoneOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: formData.phoneNumber, role: "INSTRUCTOR" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send OTP");
      setPhoneOtpSent(true);
      setPhoneResendCooldown(30);
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      setPhoneOtpError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setPhoneOtpLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!/^\d{6}$/.test(phoneOtpValue)) {
      setPhoneOtpError("Please enter the 6-digit OTP");
      return;
    }
    setPhoneOtpError("");
    setPhoneOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: formData.phoneNumber,
          otp: phoneOtpValue,
          role: "INSTRUCTOR",
          mode: "verify-only",
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPhoneVerified(true);
        setPhoneOtpSent(false);
        toast.success("Mobile number verified!");
      } else {
        setPhoneOtpError(data.error || "Invalid OTP. Please try again.");
      }
    } finally {
      setPhoneOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous server errors
    setServerError("");

    // Run validation to show all errors at once
    const isValid = validateForm();

    // If validation fails, stop here (errors are already set by validateForm)
    if (!isValid) {
      console.log("Form validation failed - showing all errors");
      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector(".border-red-500");
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/instructors/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Essential registration fields only
          fullName: formData.fullName.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phoneNumber: formData.phoneNumber.replace(/\D/g, ""),
          gender: formData.gender,
          dateOfBirth: formData.dateOfBirth,
          city: formData.city.trim(),
          state: formData.state,
          instructorType: formData.instructorType,
          yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
          termsAccepted: formData.termsAccepted,
        }),
      });

      const data = await response.json();

      console.log("Registration response:", {
        ok: response.ok,
        status: response.status,
        data: data,
      });

      if (response.ok) {
        // Set success state immediately to show success UI
        setIsSuccess(true);

        // Store auth data immediately
        if (data.data?.user) {
          // Store auth data immediately
          if (data.data?.token) {
            localStorage.setItem("auth_token", data.data.token);
          }

          // Store user data in localStorage for UI components
          localStorage.setItem("user", JSON.stringify(data.data.user));

          // Show success toast and redirect to profile
          toast.success("Account created successfully! Redirecting...", {
            duration: 1500,
          });

          // Direct redirect to profile page (no onboarding)
          router.replace("/instructors/profile");
        } else {
          // Fallback to login if no user data
          toast.success("Account created successfully! Please sign in.", {
            duration: 1500,
          });
          router.replace(
            `/instructors/login?email=${encodeURIComponent(formData.email)}&registered=true`
          );
        }
        return; // Exit early after successful registration
      } else {
        // Handle 400 and other server errors
        console.error("Registration failed:", data);
        setServerError(
          data.error || data.message || "Registration failed. Please try again."
        );
      }
    } catch (error: unknown) {
      console.error("Registration error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Registration failed. Please try again.";
      setServerError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return "bg-red-500";
    if (passwordStrength < 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 50) return "Weak";
    if (passwordStrength < 75) return "Good";
    return "Strong";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 mt-[60px] sm:mt-[80px] ">
      <div className="max-w-md w-full space-y-8 zoom-container">
        {/* Success State */}
        {isSuccess ? (
          <LoadingSpinner
            fullScreen={false}
            text="Account Created Successfully! Redirecting..."
            size="md"
          />
        ) : (
          <>
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#463a33]">
                Become an Instructor
              </h2>
              <p className="mt-2 text-[#463a33]/60">
                Join our wellness community and start teaching
              </p>
              <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-[#463a33]/60">
                <CheckCircleIcon className="h-4 w-4 text-[#FF7043]" />
                <span>Quick signup</span>
                <span>•</span>
                <CheckCircleIcon className="h-4 w-4 text-[#FF7043]" />
                <span>Complete profile later</span>
                <span>•</span>
                <CheckCircleIcon className="h-4 w-4 text-[#FF7043]" />
                <span>Start earning</span>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white/95 rounded-lg shadow-sm sm:shadow-lg p-4 sm:p-8 border border-orange-100/50 sm:border-orange-100">
              <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                {/* Full Name */}
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) =>
                        handleInputChange("fullName", e.target.value)
                      }
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.fullName
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.email
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      className={`pl-10 pr-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.password
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#463a33]/40 hover:text-[#463a33]"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#463a33]/60">
                          Password strength:
                        </span>
                        <span
                          className={`font-medium ${passwordStrength < 50
                            ? "text-red-500"
                            : passwordStrength < 75
                              ? "text-yellow-500"
                              : "text-green-500"
                            }`}
                        >
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                        <div
                          className={`h-1 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      className={`pl-10 pr-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.confirmPassword
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#463a33]/40 hover:text-[#463a33]"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Phone Number
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) =>
                        handleInputChange("phoneNumber", e.target.value)
                      }
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.phoneNumber
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="9876543210"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>

                {/* Gender and Date of Birth in a row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Gender */}
                  <div>
                    <label
                      htmlFor="gender"
                      className="block text-sm font-medium text-[#463a33] mb-2"
                    >
                      Gender
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setIsGenderDropdownOpen(!isGenderDropdownOpen)
                        }
                        className={`w-full appearance-none px-4 py-3 pr-10 rounded-xl border-2 bg-white/90 backdrop-blur-sm text-gray-700 focus:outline-none focus:ring-2 transition-all duration-300 shadow-sm hover:shadow-md text-left ${errors.gender
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                          }`}
                      >
                        {formData.gender || "Select gender"}
                      </button>
                      <ChevronDownIcon
                        className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isGenderDropdownOpen ? "rotate-180" : ""
                          }`}
                      />

                      {isGenderDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsGenderDropdownOpen(false)}
                          />
                          <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                            {[
                              "Male",
                              "Female",
                              "Other",
                              "Prefer not to say",
                            ].map((gender) => (
                              <button
                                key={gender}
                                type="button"
                                onClick={() => {
                                  handleInputChange("gender", gender);
                                  setIsGenderDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${formData.gender === gender
                                  ? "bg-orange-50 text-orange-600 font-medium"
                                  : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                                  }`}
                              >
                                {gender}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {errors.gender && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.gender}
                      </p>
                    )}
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label
                      htmlFor="dateOfBirth"
                      className="block text-sm font-medium text-[#463a33] mb-2"
                    >
                      Date of Birth
                    </label>
                    <input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) =>
                        handleInputChange("dateOfBirth", e.target.value)
                      }
                      max={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() - 18)
                        )
                          .toISOString()
                          .split("T")[0]
                      }
                      className={`block w-full px-3 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors ${errors.dateOfBirth
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                    />
                    {errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div>
                </div>

                {/* State */}
                <div>
                  <label
                    htmlFor="state"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    State
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                    <button
                      type="button"
                      onClick={() =>
                        setIsStateDropdownOpen(!isStateDropdownOpen)
                      }
                      className={`w-full appearance-none pl-11 px-4 py-3 pr-10 rounded-xl border-2 bg-white/90 backdrop-blur-sm text-gray-700 focus:outline-none focus:ring-2 transition-all duration-300 shadow-sm hover:shadow-md text-left ${errors.state
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                        }`}
                    >
                      {formData.state || "Select your state"}
                    </button>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isStateDropdownOpen ? "rotate-180" : ""
                        }`}
                    />

                    {isStateDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsStateDropdownOpen(false)}
                        />
                        <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-hidden flex flex-col">
                          <div className="p-2 border-b border-gray-100 bg-gray-50 sticky top-0 z-30">
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                value={stateSearch}
                                onChange={(e) => setStateSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                placeholder="Search state..."
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-48">
                            {INDIAN_STATES.filter((state) =>
                              state
                                .toLowerCase()
                                .includes(stateSearch.toLowerCase())
                            ).length > 0 ? (
                              INDIAN_STATES.filter((state) =>
                                state
                                  .toLowerCase()
                                  .includes(stateSearch.toLowerCase())
                              ).map((state) => (
                                <button
                                  key={state}
                                  type="button"
                                  onClick={() => {
                                    handleInputChange("state", state);
                                    setIsStateDropdownOpen(false);
                                    setStateSearch("");
                                  }}
                                  className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${formData.state === state
                                    ? "bg-orange-50 text-orange-600 font-medium"
                                    : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                                    }`}
                                >
                                  {state}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                No states found
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label
                    htmlFor="city"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    City
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                    {availableCities.length > 0 ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setIsCityDropdownOpen(!isCityDropdownOpen)
                          }
                          className={`w-full appearance-none pl-11 px-4 py-3 pr-10 rounded-xl border-2 bg-white/90 backdrop-blur-sm text-gray-700 focus:outline-none focus:ring-2 transition-all duration-300 shadow-sm hover:shadow-md text-left ${errors.city
                            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                            }`}
                        >
                          {formData.city || "Select your city"}
                        </button>
                        <ChevronDownIcon
                          className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isCityDropdownOpen ? "rotate-180" : ""
                            }`}
                        />

                        {/* City Dropdown with Search */}
                        {isCityDropdownOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setIsCityDropdownOpen(false)}
                            />
                            <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-hidden flex flex-col">
                              <div className="p-2 border-b border-gray-100 bg-gray-50 sticky top-0 z-30">
                                <div className="relative">
                                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={citySearch}
                                    onChange={(e) =>
                                      setCitySearch(e.target.value)
                                    }
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                    placeholder="Search city..."
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              <div className="overflow-y-auto max-h-48">
                                {availableCities.filter((city) =>
                                  city
                                    .toLowerCase()
                                    .includes(citySearch.toLowerCase())
                                ).length > 0 ? (
                                  availableCities
                                    .filter((city) =>
                                      city
                                        .toLowerCase()
                                        .includes(citySearch.toLowerCase())
                                    )
                                    .map((city) => (
                                      <button
                                        key={city}
                                        type="button"
                                        onClick={() => {
                                          handleInputChange("city", city);
                                          setIsCityDropdownOpen(false);
                                          setCitySearch("");
                                        }}
                                        className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${formData.city === city
                                          ? "bg-orange-50 text-orange-600 font-medium"
                                          : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                                          }`}
                                      >
                                        {city}
                                      </button>
                                    ))
                                ) : (
                                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    No cities found
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange("city", e.target.value)
                        }
                        className={`pl-11 block w-full px-4 py-3 border-2 rounded-xl bg-white/90 backdrop-blur-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${errors.city
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                          }`}
                        placeholder={
                          formData.state
                            ? "Select a state first"
                            : "Enter your city"
                        }
                        disabled={!formData.state}
                      />
                    )}
                  </div>
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>

                {/* Instructor Type */}
                <div>
                  <label className="block text-sm font-medium text-[#463a33] mb-2">
                    What do you teach?
                  </label>
                  <div
                    className={`grid grid-cols-2 gap-2 ${errors.instructorType ? "border-2 border-red-500 rounded-lg p-2" : ""}`}
                  >
                    {INSTRUCTOR_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleInstructorTypeToggle(type.value)}
                        className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${formData.instructorType.includes(type.value)
                          ? "border-[#FF7043] bg-orange-50 text-[#FF7043]"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                      >
                        <div className="font-medium">{type.label}</div>
                      </button>
                    ))}
                  </div>
                  {errors.instructorType && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.instructorType}
                    </p>
                  )}
                </div>

                {/* Years of Experience */}
                <div>
                  <label
                    htmlFor="yearsOfExperience"
                    className="block text-sm font-medium text-[#463a33] mb-2"
                  >
                    Years of Experience
                  </label>
                  <div className="relative">
                    <AcademicCapIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="yearsOfExperience"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.yearsOfExperience}
                      onChange={(e) =>
                        handleInputChange("yearsOfExperience", e.target.value)
                      }
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${errors.yearsOfExperience
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        }`}
                      placeholder="Enter years of teaching experience"
                    />
                  </div>
                  {errors.yearsOfExperience && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.yearsOfExperience}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-[#463a33]/60">
                    Enter 0 if you're just starting your teaching journey
                  </p>
                </div>

                {/* Transparency Notice */}
                <div className="rounded-md bg-green-50 p-4 border border-green-100">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CurrencyDollarIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">Transparent Earnings Model</h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>
                          Instructors currently receive 80% of the class fee for bookings completed through the platform.
                        </p>
                        <p className="mt-1">
                          Relaxing Routine retains a 20% platform fee to cover payment processing, technology, and operational support.
                        </p>

                      </div>
                    </div>
                  </div>
                </div>

                {/* Legal Agreements */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.termsAccepted}
                      onChange={(e) =>
                        handleInputChange("termsAccepted", e.target.checked)
                      }
                      className={`mt-1 rounded text-[#FF7043] focus:ring-[#FF7043] ${errors.termsAccepted
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                        }`}
                    />
                    <span className="text-sm text-[#463a33]">
                      I agree to the{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setLegalDocType("terms");
                          setShowLegalModal(true);
                        }}
                        className="text-[#FF7043] hover:text-[#FF7043]/80 underline"
                      >
                        Terms & Conditions
                      </button>{" "}
                      and{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setLegalDocType("privacy");
                          setShowLegalModal(true);
                        }}
                        className="text-[#FF7043] hover:text-[#FF7043]/80 underline"
                      >
                        Privacy Policy
                      </button>
                    </span>
                  </label>
                  {errors.termsAccepted && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.termsAccepted}
                    </p>
                  )}
                </div>

                {/* Server Error Display */}
                {serverError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{serverError}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full h-12 flex justify-center items-center py-3 px-4 border border-transparent text-white font-medium rounded-lg bg-[#FF7043] hover:bg-[#FF7043]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF7043] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" showText={false} />
                      Creating Account...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 mr-2" />
                      Create My Instructor Account
                    </div>
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div className="mt-6 text-center">
                <span className="text-[#463a33]/60">
                  Already have an account?{" "}
                </span>
                <Link
                  href="/login"
                  className="font-medium text-[#FF7043] hover:text-[#FF7043]/80 transition-colors"
                >
                  Sign in
                </Link>
              </div>
            </div>

            {/* Back to Home */}
            <div className="text-center">
              <Link
                href="/"
                className="inline-flex items-center text-[#463a33]/60 hover:text-[#463a33] transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Legal Documents Modal */}
      <LegalDocumentModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        documentType={legalDocType}
      />
    </div>
  );
};

export default InstructorRegisterPage;
